import type { Option, Unsubscribe } from "@tento-lona/sheets";
import type { LiveData } from "@tento-core/livedata";
import { Tracing } from "@tento-core/tracing";
import type { SheetElement } from "./sheet-element.ts";

/** One presentation owner for navigation and the binds it requires. Readiness
 * notifications are hints; the host rechecks the live queue and geometry at
 * the actual visibility mutation. No receipt is inferred from a notification.
 * Once initial content selection is settled, every hold is released, and the
 * presentation is already "slow", a
 * reveal predicate that stays false for `STALL_MS` more fails open: what is
 * bound is revealed rather than holding the page inert behind the overlay (a
 * visible column whose bind threw keeps the predicate false and no drain ever
 * re-checks it). The one exception is a navigation whose incoming
 * render has not arrived (`awaitRender`): what is bound is the scrubbed
 * previous sheet, so the stall presents "stalled-navigation" and stays held
 * until that render arrives (operator 2026-10-09: "hide the cell and its
 * contents and provide a basic loading spinner"). An initial prerequisite
 * (`deferInitialReveal`) that has not settled `BOOT_STALL_MS` after the
 * presentation turned "slow" is abandoned (trace `sheet/boot-reveal-stalled`),
 * so a boot that never settles cannot keep the sheet hidden for good.
 * Each "ready" is followed by `Host.revealed`: "verified" only when the live
 * reveal predicate passed for the current generation with its initial
 * prerequisite genuinely settled; a stall fail-open or an abandoned boot
 * prerequisite is "recovered", and must not be read as successful use. */
export class SheetPresentation {
  #owner = 0;
  #revision = 0;
  #active = false;
  #queued = false;
  #failedAt: Option<number> = null;
  #awaitingRender = false;
  #completedRevision = 0;
  #state: Option<SheetPresentation.State> = null;
  #holds = new Set<number>();
  #unsubscribe: Option<() => void> = null;
  #timer: Option<number> = null;
  #stallTimer: Option<number> = null;
  /** The boot backstop: armed once per initial prerequisite, not per navigation. */
  #bootStallTimer: Option<number> = null;
  /** Initial content selection is independent of replaceable navigation tickets. */
  #initialReadiness: Option<LiveData.Readable<boolean>> = null;
  /** Retires a subscription being prepared when host presentation re-enters lifecycle code. */
  #initialReadinessRevision = 0;
  /** This presentation abandoned its initial prerequisite (`#armBootStall`); its reveal is "recovered". */
  #bootAbandoned = false;

  readonly #host: SheetPresentation.Host;
  constructor(host: SheetPresentation.Host) {
    this.#host = host;
  }

  get held(): boolean { return this.#active; }

  /** Whether the content is concealed: held and not failed. A failed
   * presentation stays held (a later render can still reveal it) but shows
   * what is bound. */
  get concealing(): boolean { return this.#active && this.#state !== "failed"; }

  /** Keep the initial empty surface concealed until the host has selected its
   * first content (which may intentionally be empty); null forgets a recycled owner. Navigation generations
   * cannot retire this prerequisite; explicit presentation failures still fail open. */
  deferInitialReveal(readiness: Option<LiveData.Readable<boolean>>): void {
    this.#initialReadinessRevision++;
    this.#clearBootStall();
    this.#initialReadiness?.removeListener(this);
    this.#initialReadiness = readiness?.get() ? null : readiness;
    if (this.#initialReadiness != null) this.reconnect();
    else this.check();
  }

  /** Re-arm a detached initial prerequisite and sample changes made while detached. */
  reconnect(): void {
    const readiness = this.#initialReadiness;
    if (readiness == null) return;
    const revision = this.#initialReadinessRevision;
    const ticket = this.#active ? null : this.start();
    // Showing loading can synchronously detach/recycle the element or replace its host.
    if (!this.#active || this.#initialReadiness !== readiness || this.#initialReadinessRevision !== revision) {
      if (ticket != null) this.complete(ticket);
      return;
    }
    readiness.addListener(this, () => {
      if (!readiness.get() || this.#initialReadiness !== readiness) return;
      readiness.removeListener(this);
      this.#initialReadiness = null;
      this.check();
    });
    if (readiness.get()) {
      readiness.removeListener(this);
      this.#initialReadiness = null;
      this.check();
    }
    if (ticket != null) this.complete(ticket);
  }

  /** Stop detached consumers without forgetting an unresolved initial prerequisite. */
  disconnect(): void {
    this.#initialReadinessRevision++;
    this.#clearBootStall();
    this.#initialReadiness?.removeListener(this);
    this.cancel();
  }

  /** A live read also covers readiness changes between scheduling and mutation. */
  #initialReady(): boolean {
    return this.#initialReadiness == null || this.#initialReadiness.get();
  }

  start(): SheetPresentation.Ticket {
    this.cancel();
    this.#active = true;
    const ticket = this.#addHold();
    const owner = this.#owner;
    this.#present("loading");
    if (!this.#active || this.#owner !== owner) return ticket;
    this.#timer = this.#host.setTimeout(() => {
      this.#timer = null;
      if (!this.#active || this.#owner !== owner) return;
      this.#present("slow");
      // Every hold already released: start the stall wait now (`check`).
      if (this.#holds.size === 0 && this.#failedAt == null) this.check();
    }, 500);
    const unsubscribe = this.#host.subscribe(() => this.check());
    if (this.#active && this.#owner === owner) this.#unsubscribe = unsubscribe;
    else unsubscribe();
    this.check();
    return ticket;
  }

  hold(): SheetPresentation.Ticket {
    return this.#active ? this.#addHold() : this.start();
  }

  #addHold(): SheetPresentation.Ticket {
    const revision = ++this.#revision;
    this.#holds.add(revision);
    return { owner: this.#owner, revision };
  }

  current(ticket: SheetPresentation.Ticket): boolean {
    return this.#active && ticket.owner === this.#owner;
  }

  complete(ticket: SheetPresentation.Ticket): void {
    if (!this.current(ticket) || !this.#holds.delete(ticket.revision)) return;
    this.#completedRevision = Math.max(this.#completedRevision, ticket.revision);
    if (this.#failedAt != null && ticket.revision > this.#failedAt) this.#failedAt = null;
    this.check();
  }

  fail(ticket: SheetPresentation.Ticket): void {
    if (!this.current(ticket) || !this.#holds.delete(ticket.revision)) return;
    if (ticket.revision < this.#completedRevision) { this.check(); return; }
    this.#failedAt = ticket.revision;
    this.#present("failed");
    this.#clearTimer();
  }

  /** `ticket`'s navigation has no incoming render yet: until `renderArrived`,
   * a stall presents "stalled-navigation" instead of revealing. */
  awaitRender(ticket: SheetPresentation.Ticket): void {
    if (this.current(ticket)) this.#awaitingRender = true;
  }

  /** The incoming render arrived: its bind decides the reveal, and a later stall fails open again. */
  renderArrived(ticket: SheetPresentation.Ticket): void {
    if (this.current(ticket)) this.#awaitingRender = false;
  }

  exhausted(ticket: SheetPresentation.Ticket): void {
    if (!this.current(ticket) || !this.#holds.has(ticket.revision)) return;
    this.#present(this.#failedAt == null ? "slow" : "failed");
    this.#clearTimer();
    this.check();
  }

  check(): void {
    if (!this.#active || this.#holds.size > 0 || this.#failedAt != null) return;
    if (!this.#initialReady()) {
      if (this.#state === "slow") this.#armBootStall();
      return;
    }
    if (this.#queued) return;
    const owner = this.#owner;
    const revision = this.#revision;
    this.#queued = true;
    this.#host.afterMutate(() => {
      if (this.#owner !== owner) return;
      this.#queued = false;
      if (!this.#active || !this.#initialReady() || this.#holds.size > 0 || this.#failedAt != null) return;
      if (this.#revision !== revision) { this.check(); return; }
      try {
        if (!this.#host.ready()) {
          if (this.#state === "slow" || this.#state === "stalled-navigation") this.#armStall(owner);
          return;
        }
      } catch {
        // The host reports its measurement/adapter exception. A later drain
        // cannot turn that failed assessment into readiness without a new bind.
        if (this.#active && this.#owner === owner) {
          this.#failedAt = revision;
          this.#present("failed");
          this.#clearTimer();
        }
        return;
      }
      // ready() may synchronously trigger a host invalidation or retirement.
      if (!this.#active || !this.#initialReady() || this.#owner !== owner || this.#revision !== revision || this.#holds.size > 0) return;
      const how = this.#bootAbandoned ? "recovered" : "verified";
      this.cancel();
      this.#reveal(how);
    });
  }

  cancel(): void {
    this.#owner++;
    this.#active = false;
    this.#queued = false;
    this.#holds.clear();
    this.#failedAt = null;
    this.#awaitingRender = false;
    this.#bootAbandoned = false;
    this.#state = null;
    this.#clearTimer();
    if (this.#stallTimer != null) this.#host.clearTimeout(this.#stallTimer);
    this.#stallTimer = null;
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  /** Reveal `STALL_MS` after a "slow" check found every hold released and the
   * predicate false, if that still holds and nothing failed. A hold taken
   * meanwhile (a new bind) re-arms the wait through `check`. While the
   * navigation's render has not arrived, present "stalled-navigation" and stay
   * held instead. */
  #armStall(owner: number): void {
    if (this.#stallTimer != null) return;
    const revision = this.#revision;
    this.#stallTimer = this.#host.setTimeout(() => {
      this.#stallTimer = null;
      if (!this.#active || !this.#initialReady() || this.#owner !== owner || this.#holds.size > 0 || this.#failedAt != null) return;
      if (this.#revision !== revision) { this.check(); return; }
      if (this.#awaitingRender) {
        if (this.#state !== "stalled-navigation") Tracing.log("sheet/navigation-stalled", { stallMs: SheetPresentation.STALL_MS });
        this.#present("stalled-navigation");
        return;
      }
      Tracing.log("sheet/reveal-stalled", { stallMs: SheetPresentation.STALL_MS });
      this.cancel();
      this.#reveal("recovered");
    }, SheetPresentation.STALL_MS);
  }

  /** `BOOT_STALL_MS` after the first "slow" check that found every hold
   * released while the initial prerequisite was unsettled, abandon that
   * prerequisite and run the ordinary path: the reveal predicate, then the
   * `STALL_MS` fail-open. A replacing navigation does not restart the wait. */
  #armBootStall(): void {
    if (this.#bootStallTimer != null) return;
    const revision = this.#initialReadinessRevision;
    this.#bootStallTimer = this.#host.setTimeout(() => {
      this.#bootStallTimer = null;
      const readiness = this.#initialReadiness;
      if (readiness == null || this.#initialReadinessRevision !== revision || readiness.get()) return;
      Tracing.log("sheet/boot-reveal-stalled", { stallMs: SheetPresentation.BOOT_STALL_MS });
      readiness.removeListener(this);
      this.#initialReadiness = null;
      this.#bootAbandoned = true;
      this.check();
    }, SheetPresentation.BOOT_STALL_MS);
  }

  #clearBootStall(): void {
    if (this.#bootStallTimer != null) this.#host.clearTimeout(this.#bootStallTimer);
    this.#bootStallTimer = null;
  }

  #clearTimer(): void {
    if (this.#timer != null) this.#host.clearTimeout(this.#timer);
    this.#timer = null;
  }

  /** Present "ready", then tell the host how it was reached (after the
   * presentation mutation, so a listener reads the revealed surface). */
  #reveal(how: SheetElement.RevealHow): void {
    this.#present("ready");
    this.#host.revealed?.(how);
  }

  #present(state: SheetPresentation.State): void {
    if (this.#state === state) return;
    this.#state = state;
    this.#host.present(state);
  }
}

export namespace SheetPresentation {
  /** How long a presentation with every hold released waits for its reveal predicate before it reveals anyway. */
  export const STALL_MS = 2000;
  /** How long an unsettled initial prerequisite may conceal a "slow" presentation
   * whose holds are released before it is abandoned (four stall waits). */
  export const BOOT_STALL_MS = 4 * STALL_MS;
  /** Zero-height layout and non-owner span cells deliberately paint nothing.
   * Missing layout is not equivalent to a legitimate zero-height plan. */
  export function geometryReady(input: {
    hidden: boolean;
    spanHidden: boolean;
    plannedHeight: Option<number>;
    measuredHeight: number;
  }): boolean {
    if (input.hidden || input.spanHidden) return true;
    const planned = input.plannedHeight;
    if (planned == null || !Number.isFinite(planned) || planned < 0) return false;
    return planned === 0 || (Number.isFinite(input.measuredHeight) && input.measuredHeight > 0);
  }
  export interface Ticket { readonly owner: number; readonly revision: number }
  /** "stalled-navigation": a navigation's render has not arrived after the stall wait; still held, cells hidden, the spinner shown. */
  export type State = "loading" | "slow" | "stalled-navigation" | "failed" | "ready";
  export interface Host {
    present(state: State): void;
    /** Called after each "ready" presentation with how it was reached. */
    revealed?(how: SheetElement.RevealHow): void;
    ready(): boolean;
    subscribe(callback: () => void): Unsubscribe;
    afterMutate(callback: () => void): void;
    setTimeout(callback: () => void, milliseconds: number): number;
    clearTimeout(id: number): void;
  }
}
